/* wall-set-similar.js
 *
 * "Similar Artworks" handler for Wall Set product pages.
 *
 * Reads the same catalog JSON the single-product recommender bundle
 * uses (`window.__wallArtCatalogUrl`), then scores:
 *   1. Other Wall Sets by tag overlap with THIS set
 *   2. Single artworks by tag overlap with the UNION of this set's
 *      constituent prints' tags
 *
 * Sets are ranked above single artworks regardless of score (per spec
 * — a similar set is always more valuable than a similar single since
 * it preserves the "complete arrangement" purchase intent). Within
 * each group, items are sorted by score desc.
 *
 * Renders standard product cards via Shopify's Section Rendering API
 * (/products/<handle>?section_id=recommended-card) — same pattern as
 * wall-art-recommender.js — so the output is visually identical to
 * the "You may also like" section.
 *
 * Wiring:
 *   <div data-wall-set-similar
 *        data-set-id="..."
 *        data-product-id="..."
 *        data-constituent-design-ids="id1,id2,id3"
 *        data-count="8"
 *        data-columns-desktop="4"
 *        data-columns-mobile="2"></div>
 */

(function () {
  'use strict';

  // ── Scoring weights (mirror the single-product recommender so the
  //    look-and-feel of "what's similar" stays consistent across pages).
  var W_SUBJECT = 1.0;
  var W_STYLE   = 2.5;
  var W_COLOR   = 3.0;
  var W_ROOM    = 0.6;

  // Cap how many of each type to consider before merging — keeps the
  // Section Rendering API fan-out bounded.
  var MAX_SETS_CONSIDERED    = 12;
  var MAX_SINGLES_CONSIDERED = 24;

  // ── Catalog cache (module-level, survives multiple #data-wall-set-similar
  //    nodes on the same page; rare today but cheap to support).
  var catalogPromise = null;

  function fetchCatalog() {
    if (catalogPromise) return catalogPromise;
    var url = window.__wallArtCatalogUrl;
    if (!url) {
      catalogPromise = Promise.resolve(null);
      return catalogPromise;
    }
    catalogPromise = fetch(url, { mode: 'cors', credentials: 'omit' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .catch(function (err) {
        console.warn('[wall-set-similar] catalog fetch failed:', err);
        return null;
      });
    return catalogPromise;
  }

  // ── Scoring ────────────────────────────────────────────────────────

  function tagOverlapCount(a, b) {
    if (!a || !b || a.length === 0 || b.length === 0) return 0;
    var set = new Set(a);
    var n = 0;
    for (var i = 0; i < b.length; i++) if (set.has(b[i])) n += 1;
    return n;
  }

  /**
   * Build a "virtual design" representing the union of all constituent
   * prints' tags. Used as the comparand for similar-singles scoring.
   * Duplicates are deduplicated so a 3-print set with the same style on
   * all three doesn't get 3× weight on that style.
   */
  function buildSetSignature(designs, constituentIds) {
    var ids = new Set(constituentIds);
    var subjects = new Set();
    var styles = new Set();
    var colors = new Set();
    var rooms = new Set();
    for (var i = 0; i < designs.length; i++) {
      var d = designs[i];
      if (!ids.has(d.id)) continue;
      if (d.subjects) for (var j = 0; j < d.subjects.length; j++) subjects.add(d.subjects[j]);
      if (d.styles)   for (var k = 0; k < d.styles.length;   k++) styles.add(d.styles[k]);
      if (d.colors)   for (var l = 0; l < d.colors.length;   l++) colors.add(d.colors[l]);
      if (d.rooms)    for (var m = 0; m < d.rooms.length;    m++) rooms.add(d.rooms[m]);
    }
    return {
      subjects: Array.from(subjects),
      styles: Array.from(styles),
      colors: Array.from(colors),
      rooms: Array.from(rooms),
    };
  }

  function scoreSingle(design, signature) {
    var s = 0;
    s += W_SUBJECT * tagOverlapCount(design.subjects, signature.subjects);
    s += W_STYLE   * tagOverlapCount(design.styles,   signature.styles);
    s += W_COLOR   * tagOverlapCount(design.colors,   signature.colors);
    s += W_ROOM    * tagOverlapCount(design.rooms,    signature.rooms);
    return s;
  }

  function scoreSetVsSet(otherSet, thisSet) {
    // For set↔set, prefer the admin-curated set_style/set_room tags
    // (intentional curation signal) plus the inherited tag union.
    var s = 0;
    s += W_STYLE   * tagOverlapCount(otherSet.set_style_tags,         thisSet.set_style_tags);
    s += W_ROOM    * tagOverlapCount(otherSet.set_room_tags,          thisSet.set_room_tags);
    s += W_SUBJECT * tagOverlapCount(otherSet.inherited_subject_tags, thisSet.inherited_subject_tags);
    s += W_STYLE   * tagOverlapCount(otherSet.inherited_style_tags,   thisSet.inherited_style_tags);
    s += W_COLOR   * tagOverlapCount(otherSet.inherited_color_tags,   thisSet.inherited_color_tags);
    // Tiny bonus when print counts match — a "3-piece" customer likely
    // wants other 3-piece options. Capped to 1 so it can't flip ranking
    // among otherwise-strong candidates.
    if (otherSet.print_count === thisSet.print_count) s += 1.0;
    return s;
  }

  // ── Card fetching via Section Rendering API ────────────────────────

  var cardCache = new Map();

  function fetchCardHtml(handle) {
    if (!handle) return Promise.resolve(null);
    if (cardCache.has(handle)) return cardCache.get(handle);
    var promise = fetch('/products/' + handle + '?section_id=recommended-card', {
      credentials: 'same-origin',
    })
      .then(function (r) { return r.ok ? r.text() : null; })
      .catch(function () { return null; });
    cardCache.set(handle, promise);
    return promise;
  }

  // ── Main entry point per trigger element ───────────────────────────

  function init(el) {
    var setId = el.getAttribute('data-set-id') || '';
    var productId = el.getAttribute('data-product-id') || '';
    var constituentRaw = el.getAttribute('data-constituent-design-ids') || '';
    var constituentIds = constituentRaw.split(',').map(function (s) { return s.trim(); }).filter(Boolean);
    var count = parseInt(el.getAttribute('data-count') || '8', 10);
    if (!Number.isFinite(count) || count < 2) count = 8;

    fetchCatalog().then(function (catalog) {
      if (!catalog || !Array.isArray(catalog.designs)) {
        renderEmpty(el, 'Catalog unavailable.');
        return;
      }

      var allDesigns = catalog.designs;
      var allSets = Array.isArray(catalog.sets) ? catalog.sets : [];

      // ── Find THIS set in the catalog (for set↔set scoring) ──────────
      var thisSet = null;
      for (var i = 0; i < allSets.length; i++) {
        if (allSets[i].id === setId || String(allSets[i].shopify_product_id) === String(productId)) {
          thisSet = allSets[i];
          break;
        }
      }
      // Fallback: if catalog doesn't have this set yet (e.g. just
      // published, catalog not regenerated), synthesize a signature
      // from constituent prints so similar-singles still works.
      if (!thisSet && constituentIds.length > 0) {
        var sig = buildSetSignature(allDesigns, constituentIds);
        thisSet = {
          id: setId,
          shopify_product_id: productId,
          inherited_subject_tags: sig.subjects,
          inherited_style_tags: sig.styles,
          inherited_color_tags: sig.colors,
          set_style_tags: [],
          set_room_tags: sig.rooms,
          print_count: constituentIds.length,
        };
      }
      if (!thisSet) {
        renderEmpty(el, 'No constituent prints found.');
        return;
      }

      // ── Score similar sets ──────────────────────────────────────────
      var setScores = [];
      for (var s = 0; s < allSets.length; s++) {
        var other = allSets[s];
        if (other.id === thisSet.id) continue;
        if (String(other.shopify_product_id) === String(productId)) continue;
        if (!other.handle) continue;
        var sc = scoreSetVsSet(other, thisSet);
        if (sc > 0) setScores.push({ item: other, score: sc, kind: 'set' });
      }
      setScores.sort(function (a, b) { return b.score - a.score; });
      var topSets = setScores.slice(0, MAX_SETS_CONSIDERED);

      // ── Score similar singles ───────────────────────────────────────
      var signature = buildSetSignature(allDesigns, constituentIds);
      var excludeDesignIds = new Set(constituentIds);
      var singleScores = [];
      for (var d = 0; d < allDesigns.length; d++) {
        var design = allDesigns[d];
        if (excludeDesignIds.has(design.id)) continue;
        if (!design.handle) continue;
        var sc2 = scoreSingle(design, signature);
        if (sc2 > 0) singleScores.push({ item: design, score: sc2, kind: 'single' });
      }
      singleScores.sort(function (a, b) { return b.score - a.score; });
      var topSingles = singleScores.slice(0, MAX_SINGLES_CONSIDERED);

      // ── Merge: sets first, then singles, total capped at `count` ───
      var merged = topSets.concat(topSingles).slice(0, count);
      if (merged.length === 0) {
        renderEmpty(el, 'No similar artworks yet.');
        return;
      }

      // ── Render scaffold first (so layout doesn't jump on card load) ─
      renderScaffold(el, merged.length);

      // ── Fetch + inject card markup in parallel ──────────────────────
      var slots = el.querySelectorAll('[data-wss-slot]');
      merged.forEach(function (entry, idx) {
        var slot = slots[idx];
        if (!slot) return;
        fetchCardHtml(entry.item.handle).then(function (html) {
          if (html) {
            slot.innerHTML = html;
          } else {
            slot.innerHTML = ''; // Section Rendering API miss — leave empty
          }
        });
      });
    });
  }

  function renderEmpty(el, msg) {
    el.innerHTML = '<p style="color:var(--color-foreground); opacity:0.55; text-align:center; padding:1rem 0; font-size:0.9rem;">' + msg + '</p>';
  }

  function renderScaffold(el, n) {
    var colsDesktop = parseInt(el.getAttribute('data-columns-desktop') || '4', 10);
    var colsMobile  = parseInt(el.getAttribute('data-columns-mobile') || '2', 10);
    if (!Number.isFinite(colsDesktop) || colsDesktop < 1) colsDesktop = 4;
    if (!Number.isFinite(colsMobile)  || colsMobile  < 1) colsMobile  = 2;
    var items = '';
    for (var i = 0; i < n; i++) {
      items += '<li class="grid__item" data-wss-slot></li>';
    }
    el.innerHTML =
      '<ul class="grid product-grid grid--' + colsDesktop + '-col-desktop grid--' + colsMobile + '-col-tablet-down" role="list">' +
      items +
      '</ul>';
  }

  // ── Bootstrap ──────────────────────────────────────────────────────

  function boot() {
    var triggers = document.querySelectorAll('[data-wall-set-similar]');
    for (var i = 0; i < triggers.length; i++) init(triggers[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
